import { ApiResult, HttpClient } from "../http.js";
import {
  CreateTenderAnalysisRequest,
  TenderQuestionRun,
  RecordTenderFeedbackRequest,
  TenderAnalysisDto,
  TenderFactRegister,
  TenderFactsView,
  TenderPlanMap,
  RecordTenderFactRequest,
  TenderProcurementRecipe,
  TenderProcurementPreview,
  TenderAiUsageSummaryDto,
  TenderAnalysisListItemDto,
  TenderChatMessageDto,
  TenderCostEstimateDto,
  TenderDocumentReadUrlDto,
  TenderDocumentInput,
  TenderFeedbackDto,
  TenderTradeDto,
  TenderUploadFileRequest,
  TenderUploadPlanDto,
  UpdateTenderTakeoffItemRequest,
} from "../types/tenders.js";

/**
 * Udbudsanalyse — whole-folder tender analysis.
 *
 * Files never pass through this API. `prepareUpload` returns one signed write URL per file,
 * the browser PUTs the bytes straight to storage, and only the resulting blob paths come
 * back to `estimate` and `create`. A tender folder is routinely hundreds of megabytes across
 * dozens of files, which no request-size limit would hold.
 */
export class TendersApi {
  constructor(private readonly http: HttpClient) {}

  planMap(id: string): Promise<ApiResult<TenderPlanMap>> {
    return this.http.get<TenderPlanMap>(`/api/tender-analyses/${encodeURIComponent(id)}/plan-map`);
  }

  savePlanMap(id: string, expectedRevision: number, rooms: TenderPlanMap['rooms']): Promise<ApiResult<TenderPlanMap>> {
    return this.http.put<TenderPlanMap>(`/api/tender-analyses/${encodeURIComponent(id)}/plan-map`, { body: { expectedRevision, rooms } });
  }

  stop(id: string): Promise<ApiResult<boolean>> {
    return this.http.post<boolean>(`/api/tender-analyses/${encodeURIComponent(id)}/stop`);
  }

  capabilities(): Promise<ApiResult<{ evidencePipeline: boolean }>> {
    return this.http.get<{ evidencePipeline: boolean }>("/api/tender-analyses/capabilities");
  }

  startFullAnalysis(id: string, includeDrawings: boolean): Promise<ApiResult<TenderAnalysisDto>> {
    return this.http.post<TenderAnalysisDto>(`/api/tender-analyses/${encodeURIComponent(id)}/full-analysis`, { body: { includeDrawings } });
  }

  questionRuns(id: string): Promise<ApiResult<TenderQuestionRun[]>> {
    return this.http.get<TenderQuestionRun[]>(`/api/tender-analyses/${encodeURIComponent(id)}/question-runs`);
  }

  prepareUpload(files: TenderUploadFileRequest[]): Promise<ApiResult<TenderUploadPlanDto>> {
    return this.http.post<TenderUploadPlanDto>("/api/tender-analyses/uploads", { body: { files } });
  }

  /** What the folder will cost, before committing to it. Charges nothing. */
  estimate(documents: TenderDocumentInput[]): Promise<ApiResult<TenderCostEstimateDto>> {
    return this.http.post<TenderCostEstimateDto>("/api/tender-analyses/estimate", {
      body: { documents },
    });
  }

  create(body: CreateTenderAnalysisRequest): Promise<ApiResult<TenderAnalysisDto>> {
    return this.http.post<TenderAnalysisDto>("/api/tender-analyses", { body });
  }

  list(projectId: string): Promise<ApiResult<TenderAnalysisListItemDto[]>> {
    return this.http.get<TenderAnalysisListItemDto[]>("/api/tender-analyses", {
      query: { projectId },
    });
  }

  listAll(): Promise<ApiResult<TenderAnalysisListItemDto[]>> {
    return this.http.get<TenderAnalysisListItemDto[]>("/api/analysis-overview/tenders");
  }

  listStandalone(): Promise<ApiResult<TenderAnalysisListItemDto[]>> {
    return this.http.get<TenderAnalysisListItemDto[]>("/api/tender-analyses", {
      query: { scope: "standalone" },
    });
  }

  /** A short-lived link to one document, so a citation can be opened rather than only read. */
  documentReadUrl(id: string, documentId: string): Promise<ApiResult<TenderDocumentReadUrlDto>> {
    return this.http.get<TenderDocumentReadUrlDto>(
      `/api/tender-analyses/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/read-url`,
    );
  }

  get(id: string): Promise<ApiResult<TenderAnalysisDto>> {
    return this.http.get<TenderAnalysisDto>(`/api/tender-analyses/${encodeURIComponent(id)}`);
  }

  /** Recorded requests across runs, including paid failures and explicit incomplete usage. */
  usage(id: string): Promise<ApiResult<TenderAiUsageSummaryDto>> {
    return this.http.get<TenderAiUsageSummaryDto>(`/api/tender-analyses/${encodeURIComponent(id)}/usage`);
  }

  selectOfferSource(id: string, body: { documentId: string; revision: string | null }): Promise<ApiResult<TenderAnalysisDto>> {
    return this.http.post<TenderAnalysisDto>(`/api/tender-analyses/${encodeURIComponent(id)}/offer-source`, { body });
  }

  procurementPreview(id: string, itemId: string, body: TenderProcurementRecipe): Promise<ApiResult<TenderProcurementPreview>> {
    return this.http.post<TenderProcurementPreview>(`/api/tender-analyses/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/procurement-preview`, { body });
  }

  facts(id: string): Promise<ApiResult<TenderFactsView>> { return this.http.get<TenderFactsView>(`/api/tender-analyses/${encodeURIComponent(id)}/facts`); }
  recordFact(id: string, body: RecordTenderFactRequest): Promise<ApiResult<TenderFactRegister>> { return this.http.post<TenderFactRegister>(`/api/tender-analyses/${encodeURIComponent(id)}/facts`, { body }); }
  acceptFact(id: string, factId: string): Promise<ApiResult<TenderFactRegister>> { return this.http.post<TenderFactRegister>(`/api/tender-analyses/${encodeURIComponent(id)}/facts/${encodeURIComponent(factId)}/accept`); }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/tender-analyses/${encodeURIComponent(id)}`);
  }

  /** The platform trade taxonomy. Served by the API so the list is never hard-coded twice. */
  listTrades(): Promise<ApiResult<TenderTradeDto[]>> {
    return this.http.get<TenderTradeDto[]>("/api/tender-analyses/trades");
  }

  /**
   * Re-runs only the fagopdeling stage against the stored corpus. Free of credits, capped per
   * analysis; 409 when the analysis is not completed or the cap is reached.
   */
  rerunScope(id: string): Promise<ApiResult<TenderAnalysisDto>> {
    return this.http.post<TenderAnalysisDto>(
      `/api/tender-analyses/${encodeURIComponent(id)}/rerun-scope`,
    );
  }

  retry(id: string): Promise<ApiResult<TenderAnalysisDto>> {
    return this.http.post<TenderAnalysisDto>(`/api/tender-analyses/${encodeURIComponent(id)}/retry`);
  }

  updateTakeoffItem(
    analysisId: string,
    itemId: string,
    body: UpdateTenderTakeoffItemRequest,
  ): Promise<ApiResult<TenderAnalysisDto>> {
    return this.http.put<TenderAnalysisDto>(
      `/api/tender-analyses/${encodeURIComponent(analysisId)}/takeoff-items/${encodeURIComponent(itemId)}`,
      { body },
    );
  }

  createCalculation(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/tender-analyses/${encodeURIComponent(id)}/create-calculation`,
    );
  }

  createTilbudsliste(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/tender-analyses/${encodeURIComponent(id)}/create-tilbudsliste`,
    );
  }

  /**
   * Promotes the analysis into a Tilbud. Idempotent — calling it again returns the quote the
   * first call created rather than minting a second one.
   *
   * Pass no quoteNumber to have the server allocate from the org's sequence.
   */
  createQuote(id: string, quoteNumber?: string | null): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/tender-analyses/${encodeURIComponent(id)}/create-quote`, {
      body: { quoteNumber },
    });
  }

  chatHistory(id: string): Promise<ApiResult<TenderChatMessageDto[]>> {
    return this.http.get<TenderChatMessageDto[]>(
      `/api/tender-analyses/${encodeURIComponent(id)}/chat`,
    );
  }

  ask(id: string, question: string): Promise<ApiResult<TenderChatMessageDto[]>> {
    return this.http.post<TenderChatMessageDto[]>(
      `/api/tender-analyses/${encodeURIComponent(id)}/chat`,
      { body: { question } },
    );
  }

  askV2(id: string, question: string): Promise<ApiResult<TenderChatMessageDto[]>> {
    return this.http.post<TenderChatMessageDto[]>(
      `/api/tender-analyses/${encodeURIComponent(id)}/chat-v2`,
      { body: { question } },
    );
  }

  /**
   * Records a correction, rule or edit — the org's learning input. A retag or a relevance
   * override also mutates the analysis in the same request, so refetch the detail afterwards.
   */
  recordFeedback(
    analysisId: string,
    body: RecordTenderFeedbackRequest,
  ): Promise<ApiResult<TenderFeedbackDto>> {
    return this.http.post<TenderFeedbackDto>(
      `/api/tender-analyses/${encodeURIComponent(analysisId)}/feedback`,
      { body },
    );
  }

  /** The org's rules and corrections, newest first. */
  listFeedback(
    params: { active?: boolean; skip?: number; take?: number } = {},
  ): Promise<ApiResult<TenderFeedbackDto[]>> {
    return this.http.get<TenderFeedbackDto[]>("/api/tender-analyses/feedback", {
      query: {
        active: params.active ?? true,
        skip: params.skip ?? 0,
        take: params.take ?? 100,
      },
    });
  }

  /** Deactivates a rule. Soft delete — the row stays for evaluation, but stops being applied. */
  deactivateFeedback(feedbackId: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(
      `/api/tender-analyses/feedback/${encodeURIComponent(feedbackId)}`,
    );
  }
}
